import React from 'react';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import moment from 'moment';
import cn from 'classnames';

import {
  findPurchase,
  fetchPurchaseWithAllLineItems,
  setPurchaseDueDate,
  setPurchaseLocked,
} from 'actions/purchases';
import {
  setPurchaseOverdue,
  setPurchasePaymentArrangementOverdue,
  setPurchaseOpen,
} from 'api';
import { getIncludedInGroups, getLoggedInUser } from 'reducers';
import { prettyKey } from 'helpers/formatters';
import SetOverdue from '../SetOverdue/SetOverdue';
import PurchaseItemList from '../PurchaseItemList/PurchaseItemList';
import PurchaseMerge from '../PurchaseMerge/PurchaseMerge';
import ApplyCredit from '../ApplyCredit';
import './style.css';

import { combinePurchaseWithSubs } from '../../helpers/purchases';
import PurchaseCsvLineItemsButton from './PurchaseCsvLineItemsButton';
import PurchaseLineItemsFilters from './PurchaseLineItemsFilters';
import SetOpen from 'components/SetOpen/SetOpen';
import SetPurchaseDueDate from 'components/SetPurchaseDueDate';
import EditShippingDetailsModal from 'components/modals/EditShippingDetailsModal';
import TogglePurchaseLocked from './TogglePurchaseLocked';
import AllocateLineItemsModal from 'components/modals/AllocateLineItemsModal';
import { can, permActions } from 'permissions';

const tableRows = [
  'id',
  'purchase_type',
  'cycle_dates',
  'shipping_full_name',
  'shipping_address1',
  'shipping_address2',
  'shipping_city_state',
  'shipping_zip_code',
  'shipping_service',
  'shipping_carrier',
  'payment_date',
  'purchase_date',
  'charge_id',
  'charge_type',
  'charge_card',
  'amount',
  'economic_adjustment_fees',
  'admin_fees',
  'taxes',
  'total',
];

const Purchase = ({
  loggedInUser,
  user,
  purchase,
  subs,
  pendingArrangementAgent,
  findPurchase,
  fetchPurchaseWithAllLineItems,
  isAdmin,
  isManager,
  isBillingAgent,
  lineItems,
  lineItemsPage,
  lineItemsCount,
  setPurchaseDueDate,
  qsSearch,
  setPurchaseLocked,
}) => {
  const purchaseWithSubs = combinePurchaseWithSubs(purchase, lineItems, subs);
  const isOverdue = purchaseWithSubs.status === 'overdue';
  const isOpen = purchaseWithSubs.status === 'open';
  const isArranged = purchaseWithSubs.status === 'arranged';
  const isPending = purchaseWithSubs.status === 'pending';
  const isComplete = purchaseWithSubs.status === 'complete';
  const isCancelled = purchaseWithSubs.status === 'cancelled';
  const isArrangedPending = purchaseWithSubs.status === 'pending_arrangement';
  const isArrangedDenied = purchaseWithSubs.status === 'denied_arrangement';
  const showSetOverdue =
    !isOverdue &&
    !isComplete &&
    !isCancelled &&
    can(loggedInUser, permActions.SET_OVERDUE_PURCHASE);
  const isB2b = ['BUSINESS', 'RESELLER'].includes(user.acl_group_name);
  const showSetOpen = isB2b && (isPending || isOverdue);
  const showDueDate = isB2b && (isPending || isOpen || isOverdue);
  const showToggleLocked = isAdmin && (isPending || isOpen || isOverdue);
  const showAllocateLineitems =
    (isOverdue || isOpen || isArranged || isComplete) &&
    can(loggedInUser, permActions.ALLOCATE_LINEITEMS);

  const { line_items } = purchaseWithSubs;

  const setFunctionInProps = (isArrangedPending, isArrangedDenied, id) => {
    return isArrangedPending || isArrangedDenied
      ? setPurchasePaymentArrangementOverdue(id)
      : setPurchaseOverdue(id);
  };

  const handleSetOpen = (id) => {
    return setPurchaseOpen(id);
  };

  return (
    <div>
      <h1>Purchase Details</h1>
      <table className="table">
        <tbody>
          {tableRows.map((attr) => {
            if (attr === 'cycle_dates') {
              return (
                <tr key={attr}>
                  <td>
                    <strong>{prettyKey(attr)}</strong>
                  </td>
                  <td>
                    {purchase.cycle_start_date || purchase.cycle_end_date
                      ? `${moment(purchase.cycle_start_date).format('MM-DD-YYYY')} - ${moment(purchase.cycle_end_date).format('MM-DD-YYYY')}`
                      : ''}
                  </td>
                  <td></td>
                </tr>
              );
            } else if (attr === 'shipping_full_name') {
              return (
                <tr key={attr}>
                  <td>
                    <strong>{prettyKey(attr)}</strong>
                  </td>
                  <td>
                    {typeof purchaseWithSubs[attr] !== 'undefined' &&
                      purchaseWithSubs[attr] !== null &&
                      purchaseWithSubs[attr].toString()}
                  </td>
                  <td>
                    <EditShippingDetailsModal
                      isB2b={isB2b}
                      purchase={purchase}
                    />
                  </td>
                </tr>
              );
            } else {
              return (
                <tr key={attr}>
                  <td>
                    <strong>{prettyKey(attr)}</strong>
                  </td>
                  <td>
                    {typeof purchaseWithSubs[attr] !== 'undefined' &&
                      purchaseWithSubs[attr] !== null &&
                      purchaseWithSubs[attr].toString()}
                  </td>
                  <td></td>
                </tr>
              );
            }
          })}
          <tr>
            <td style={{ minWidth: '200px' }}>
              <strong>Status</strong>
            </td>
            <td>{purchaseWithSubs.status.toString()}</td>
            <td>
              <span className="d-flex">
                {showSetOverdue && (
                  <SetOverdue
                    purchaseId={purchase.id}
                    onPress={(id) =>
                      setFunctionInProps(
                        isArrangedPending,
                        isArrangedDenied,
                        id,
                      )
                    }
                    onPressComplete={findPurchase}
                  />
                )}
                {showSetOpen && (
                  <SetOpen
                    purchaseId={purchase.id}
                    onPress={handleSetOpen}
                    onPressComplete={findPurchase}
                  />
                )}
              </span>
            </td>
          </tr>
          <tr>
            <td className={cn({ 'text-danger': purchase.locked })}>
              <b>Locked</b>
            </td>
            <td className={cn({ 'text-danger': purchase.locked })}>
              {String(purchase.locked)}
            </td>
            <td>
              {showToggleLocked && (
                <TogglePurchaseLocked
                  setPurchaseLocked={setPurchaseLocked}
                  purchase={purchase}
                />
              )}
            </td>
          </tr>
          <tr>
            <td>
              <strong>Due Date</strong>
            </td>
            <td>
              {purchaseWithSubs.due_date
                ? moment(purchaseWithSubs.due_date).format('MM-DD-YYYY')
                : ''}
            </td>
            <td>
              {showDueDate && (
                <SetPurchaseDueDate
                  purchase={purchase}
                  setPurchaseDueDate={setPurchaseDueDate}
                />
              )}
            </td>
          </tr>
          {isArrangedPending && (
            <tr>
              <td>
                <strong>Agent name</strong>
              </td>
              <td>
                {pendingArrangementAgent &&
                  pendingArrangementAgent.first_name +
                    ' ' +
                    pendingArrangementAgent.last_name}
              </td>
              <td></td>
            </tr>
          )}
          {(isArranged || isArrangedPending) && (
            <tr>
              <td>
                <strong>Arranged Charge Date</strong>
              </td>
              <td>{purchaseWithSubs.arranged_charge_date.toString()}</td>
              <td></td>
            </tr>
          )}
          {purchaseWithSubs.chargeback_for && (
            <React.Fragment>
              <tr>
                <td>
                  <strong>Chargeback For</strong>
                </td>
                <td>{purchaseWithSubs.chargeback_for}</td>
                <td></td>
              </tr>
              <tr>
                <td>
                  <strong>Chargeback For Status</strong>
                </td>
                <td>{purchaseWithSubs.chargeback_for_status}</td>
                <td></td>
              </tr>
            </React.Fragment>
          )}
          {purchaseWithSubs.refund_for && (
            <tr>
              <td>
                <strong>Refund For</strong>
              </td>
              <td>{purchaseWithSubs.refund_for}</td>
              <td></td>
            </tr>
          )}
          {purchaseWithSubs.refunded_by &&
            Boolean(purchaseWithSubs.refunded_by.length) && (
              <tr>
                <td>
                  <strong>Refunded By</strong>
                </td>
                <td>{purchaseWithSubs.refunded_by.join(', ')}</td>
                <td></td>
              </tr>
            )}
        </tbody>
      </table>
      {(isPending || isOverdue || isOpen) && isAdmin && (
        <ApplyCredit purchase={purchase} />
      )}
      <PurchaseLineItemsFilters
        qsSearch={qsSearch}
        lineItemsCount={lineItemsCount}
      />
      <PurchaseItemList
        items={line_items}
        purchase={purchase}
        findPurchase={findPurchase}
        isAdmin={isAdmin}
        isManager={isManager}
        isBillingAgent={isBillingAgent}
        lineItemsPage={lineItemsPage}
        lineItemsCount={lineItemsCount}
        pageBaseUrl={`/purchases/${purchase.id}/view`}
        qsSearch={qsSearch}
        isViewPage
      />
      {(isOverdue || isOpen) && (
        <PurchaseMerge purchase={purchase} onMergeComplete={findPurchase} />
      )}
      {showAllocateLineitems && (
        <AllocateLineItemsModal purchaseId={purchase.id} />
      )}
      {!isComplete && (
        <Link to={`/purchases/${purchase.id}/merge`}>
          <div className="mar-b">
            <button type="button" className="btn btn-primary">
              Merge other purchases into this one
            </button>
          </div>
        </Link>
      )}

      <PurchaseCsvLineItemsButton
        purchase={purchaseWithSubs}
        fetchPurchaseWithAllLineItems={fetchPurchaseWithAllLineItems}
      />
    </div>
  );
};

const mapDispatchToProps = {
  findPurchase,
  fetchPurchaseWithAllLineItems,
  setPurchaseDueDate,
  setPurchaseLocked,
};

const mapStateToProps = (state) => ({
  loggedInUser: getLoggedInUser(state),
  isAdmin: getIncludedInGroups(state, ['ADMIN']),
  isManager: getIncludedInGroups(state, ['MANAGER']),
  isBillingAgent: getIncludedInGroups(state, ['BILLING']),
});

export default connect(mapStateToProps, mapDispatchToProps)(Purchase);
